import { useState } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Row from "@amzn/meridian/esm/components/row";
import Select, { SelectOption } from "@amzn/meridian/esm/components/select";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import { PILL_RADIUS_FILL } from "../../constants";
import {
  EXTERNAL_PARTICIPANTS_LABEL,
  EXTERNAL_PARTICIPANTS_VALUE,
} from "./whosWho.constants";
import { formatWhosWhoCountLabel } from "./whosWho.utils";

const ALL_ORGS_VALUE = 0;
const ALL_ORGS_LABEL = "All orgs";
const ORG_COLOR_MARKER_SIZE = 8;
const EMPTY_LOOKUP = Object.freeze(Object.create(null));

const renderOrgLabel = (org, count, color) => (
  <Row spacing="200" alignmentVertical="center">
    <Theme
      tokens={{
        boxBorderRadiusFill: PILL_RADIUS_FILL,
        boxBorderColorFill: color,
      }}
    >
      <Box
        type="fill"
        backgroundColor={color}
        width={ORG_COLOR_MARKER_SIZE}
        minWidth={ORG_COLOR_MARKER_SIZE}
        height={ORG_COLOR_MARKER_SIZE}
      />
    </Theme>
    <Box>{formatWhosWhoCountLabel(org, count)}</Box>
  </Row>
);

const WhosWhoOrgSelect = ({
  orgs = [],
  orgColorMap = EMPTY_LOOKUP,
  orgCounts = EMPTY_LOOKUP,
  externalCount = 0,
  totalCount = 0,
  activeFilter,
  onSelect,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
  const allOrgsLabel = formatWhosWhoCountLabel(ALL_ORGS_LABEL, totalCount);
  const externalParticipantsLabel = formatWhosWhoCountLabel(
    EXTERNAL_PARTICIPANTS_LABEL,
    externalCount
  );
  const allOptionMatches = !normalizedQuery
    || ALL_ORGS_LABEL.toLocaleLowerCase().includes(normalizedQuery);
  const externalOptionMatches = externalCount > 0 && (
    !normalizedQuery
    || EXTERNAL_PARTICIPANTS_LABEL.toLocaleLowerCase().includes(normalizedQuery)
  );
  const matchingOrgs = normalizedQuery
    ? orgs.filter((org) => org.toLocaleLowerCase().includes(normalizedQuery))
    : orgs;
  const hasMatches = allOptionMatches || externalOptionMatches || matchingOrgs.length > 0;
  const selectedLabel = activeFilter === EXTERNAL_PARTICIPANTS_VALUE
    ? externalParticipantsLabel
    : activeFilter
      ? renderOrgLabel(activeFilter, orgCounts[activeFilter], orgColorMap[activeFilter])
      : allOrgsLabel;

  return (
    <Select
      size="small"
      searchQuery={searchQuery}
      onSearch={setSearchQuery}
      placeholder="Search organizations or participant types"
      value={activeFilter ?? ALL_ORGS_VALUE}
      valueLabel={() => selectedLabel}
      onChange={(value) => onSelect(value === ALL_ORGS_VALUE ? null : value)}
      width="100%"
    >
      {allOptionMatches && (
        <SelectOption value={ALL_ORGS_VALUE} label={allOrgsLabel} />
      )}
      {matchingOrgs.map((org) => {
        const label = formatWhosWhoCountLabel(org, orgCounts[org]);
        return (
          <SelectOption key={org} value={org} label={label}>
            {() => renderOrgLabel(org, orgCounts[org], orgColorMap[org])}
          </SelectOption>
        );
      })}
      {externalOptionMatches && (
        <SelectOption
          value={EXTERNAL_PARTICIPANTS_VALUE}
          label={externalParticipantsLabel}
        />
      )}
      {!hasMatches && (
        <Box spacingInset="400">
          <Text>No matching options</Text>
        </Box>
      )}
    </Select>
  );
};

export default WhosWhoOrgSelect;
